import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  appendHomeData,
  selectHome,
  setHomeData,
  setHomeFailed,
  setHomeLoading,
  touchHomeData,
} from "../../redux/reducers/homeReducer";

import { getHomeData } from "../../services/homeService";
import { isCacheFresh } from "./cachePolicy";
import { selectUser } from "../../redux/reducers/userReducer";
import { useLoader } from "../useLoader";

// The API sometimes returns an empty page with hasNextPage=true (e.g. days
// with no visible briefings for the viewer) — skip ahead a bounded number of
// times to find content.
const MAX_EMPTY_PAGE_SKIPS = 5;

export const useFetchHomeData = () => {
  const dispatch = useDispatch();
  const home = useSelector(selectHome);
  const user = useSelector(selectUser);
  const { showLoading } = useLoader();

  const email = user?.data?.user?.user_id || "";
  const revalidating = useRef(false);

  const fetchPageBody = useCallback(
    async (page: number) => {
      const params: any = { page };
      if (email) {
        params.email = email;
      }
      const response = await getHomeData(params);
      return response.data;
    },
    [email],
  );

  // Called by usePagination on scroll-bottom. Its internal counter resets on
  // every mount, so ignore the page it passes and use the slice's cursor —
  // with a warm cache holding N pages, the next fetch must be page N+1.
  const fetchData = useCallback(
    async (_page?: number) => {
      if (!home.data?.pagination?.hasNextPage) return;
      let page = home.currentPage;
      try {
        for (let skips = 0; skips <= MAX_EMPTY_PAGE_SKIPS; skips++) {
          page += 1;
          const body = await fetchPageBody(page);
          dispatch(appendHomeData({ body, page }));
          if (body?.data?.length || !body?.pagination?.hasNextPage) break;
        }
      } catch (error) {
        dispatch(setHomeFailed());
      }
    },
    [dispatch, fetchPageBody, home.currentPage, home.data?.pagination?.hasNextPage],
  );

  // First page load, with the empty-page skip behavior of the old hook.
  const loadFirstPage = useCallback(async () => {
    let page = 1;
    let body = await fetchPageBody(page);
    while (
      !body?.data?.length &&
      body?.pagination?.hasNextPage &&
      page <= MAX_EMPTY_PAGE_SKIPS
    ) {
      page += 1;
      body = await fetchPageBody(page);
    }
    return { body, page };
  }, [fetchPageBody]);

  useEffect(() => {
    const cacheValid = !!home.data && home.cachedForEmail === email;

    const run = async () => {
      if (!cacheValid) {
        // Cold start (or the user changed): loader + fetch.
        try {
          showLoading(true);
          dispatch(setHomeLoading());
          const { body, page } = await loadFirstPage();
          dispatch(setHomeData({ body, email, page }));
        } catch (error) {
          dispatch(setHomeFailed());
        } finally {
          showLoading(false);
        }
        return;
      }

      // Warm cache: render instantly, revalidate silently in the background —
      // unless the cache is fresh, in which case skip the network entirely.
      if (isCacheFresh(home.lastFetched)) return;
      if (revalidating.current) return;
      revalidating.current = true;
      try {
        const { body, page } = await loadFirstPage();
        const freshFirstId = body?.data?.[0]?.id;
        const cachedFirstId = home.data?.data?.[0]?.id;
        if (freshFirstId && freshFirstId !== cachedFirstId) {
          // New content published — replace the cache (pagination resets).
          dispatch(setHomeData({ body, email, page }));
        } else {
          dispatch(touchHomeData());
        }
      } catch (error) {
        // Background refresh failing is invisible; keep serving the cache.
      } finally {
        revalidating.current = false;
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  return { homeData: home.data, fetchData };
};
